import { Theme, useTheme } from '@mui/material';
import MuiAvatar, {
    avatarClasses,
    AvatarProps as MuiAvatarProps
} from '@mui/material/Avatar';
import * as React from 'react';

import { getReadableTextColor } from '../color';
import { getIcon, IconProps, IconType } from '../icon';
import { styled } from '../styled';
import { ColorProp, usePaletteColor } from '../theme';
import { BaseProps, enumType, makeEnum, withVersion } from '../utils';

export const AvatarSize = makeEnum(18, 24, 32, 40, 60, 80, 120, 160);
export type AvatarSize = enumType<typeof AvatarSize>;

export type AvatarProps = TypedOmit<
    MuiAvatarProps,
    'color' | 'title' | 'children'
> &
    BaseProps & {
        color?: ColorProp;
        size: AvatarSize;
        icon?: IconType;
        active?: boolean;
        interactive?: boolean;
        disabled?: boolean;
    } & (
        | {
              title?: never;
              children: React.ReactNode;
          }
        | { title: string | undefined; children?: never }
    );

const AVATAR_FILL: ColorProp = 'components.avatar.fill';
const AVATAR_DISABLED_FILL: ColorProp = 'components.avatar.disabled';

interface StyledAvatarProps {
    $active: boolean;
    $interactive: boolean;
    $size: AvatarSize;
    $variant: NonNullable<MuiAvatarProps['variant']>;
    $contentColor: string;
    $outlineColor: string;
    $hoverColor?: string;
    $focusColor?: string;
    $disabled: boolean;
}

const getVariantBorderRadius = (
    theme: Theme,
    variant: NonNullable<MuiAvatarProps['variant']>
) => {
    switch (variant) {
        case 'rounded':
            return theme.shape.borderRadius;
        case 'square':
            return 0;
        case 'circular':
        default:
            return '50%';
    }
};

const StyledAvatar = styled(MuiAvatar)<StyledAvatarProps>(({
    $active,
    $contentColor,
    $disabled,
    $focusColor,
    $interactive,
    $hoverColor,
    $outlineColor,
    $size,
    $variant,
    theme
}) => {
    const activeInnerRingWidth = theme.spacing(0.25);
    const activeOuterRingWidth = theme.spacing(0.5);
    const interactiveRingWidth = theme.spacing(0.5);
    const activeTotalRingWidth = `calc(${activeInnerRingWidth} + ${activeOuterRingWidth})`;
    const borderRadius = getVariantBorderRadius(theme, $variant);
    const activeShadow = $active
        ? `0 0 0 ${activeInnerRingWidth} ${$contentColor}, 0 0 0 ${activeTotalRingWidth} ${$outlineColor}`
        : undefined;
    const hoverShadow = $hoverColor
        ? `${activeShadow ? `${activeShadow}, ` : ''}0 0 0 ${interactiveRingWidth} ${$hoverColor}`
        : activeShadow;
    const focusShadow = $focusColor
        ? `${activeShadow ? `${activeShadow}, ` : ''}0 0 0 ${interactiveRingWidth} ${$focusColor}`
        : activeShadow;

    return {
        width: $size,
        height: $size,
        overflow: 'visible',
        position: 'relative',
        borderRadius,
        boxShadow: activeShadow,
        ...(Boolean($interactive) && {
            '&:hover': {
                boxShadow: hoverShadow
            },
            '&:focus-visible': {
                outline: 'none',
                boxShadow: focusShadow
            }
        }),
        [`& .${avatarClasses.img}`]: {
            borderRadius,
            filter: $disabled ? 'grayscale(1)' : undefined
        }
    };
});

const AvatarImpl = React.forwardRef<HTMLDivElement, AvatarProps>(
    (props, ref) => {
        const {
            size = 40,
            color = AVATAR_FILL,
            active = false,
            interactive: interactiveProp,
            variant = 'circular',
            title,
            children: _children,
            icon,
            src,
            disabled = false,
            sx: muiSx,
            ...muiProps
        } = props;
        const { components } = useTheme();
        const contentColor = usePaletteColor('components.content.fill');
        const bgColor = usePaletteColor(
            disabled ? AVATAR_DISABLED_FILL : color
        );
        const iconColor = usePaletteColor('components.avatar.icon');
        const outlineColor = usePaletteColor('components.avatar.active-border');
        const hoverColor = usePaletteColor('primary._states.hover');
        const focusColor = usePaletteColor('primary._states.focus');
        const textDisabledColor = usePaletteColor('text.disabled');
        const interactive =
            !disabled && (interactiveProp ?? Boolean(props.onClick));
        const isActive = !disabled && active;
        const preferredTextColor = disabled ? textDisabledColor : iconColor;
        const textColor = (() => {
            if (bgColor && preferredTextColor) {
                return getReadableTextColor(bgColor, preferredTextColor);
            }

            return preferredTextColor;
        })();
        const font = (() => {
            switch (size) {
                case 160:
                case 120:
                case 80:
                case 60:
                case 40:
                    return components?.avatar?.['initials-md'];
                case 32:
                    return components?.avatar?.['initials-md'];
                case 24:
                case 18:
                    return components?.avatar?.['initials-sm'];
            }
        })();
        const iconSize = ((): IconProps['fontSize'] => {
            switch (size) {
                case 160:
                case 120:
                case 80:
                case 60:
                    return 'large';
                case 40:
                    return 'medium';
                case 32:
                case 24:
                case 18:
                    return 'small';
            }
        })();
        const Icon = icon ? getIcon(icon) : null;
        const children = Icon ? (
            <Icon color={() => textColor} fontSize={iconSize} />
        ) : (
            _children
        );
        const tabIndex =
            interactive && muiProps.tabIndex === undefined
                ? 0
                : muiProps.tabIndex;

        return (
            <StyledAvatar
                ref={ref}
                data-role="avatar"
                $active={isActive}
                $interactive={interactive}
                $size={size}
                $variant={variant}
                $contentColor={contentColor}
                $outlineColor={outlineColor}
                $hoverColor={hoverColor}
                $focusColor={focusColor}
                $disabled={disabled}
                sx={{
                    bgcolor: bgColor,
                    color: textColor,
                    cursor: interactive ? 'pointer' : undefined,
                    ...muiSx
                }}
                tabIndex={tabIndex}
                variant={variant}
                src={src}
                {...muiProps}
            >
                {children ?? <span style={font}>{getInitials(title)}</span>}
            </StyledAvatar>
        );
    }
);

export const Avatar = withVersion(AvatarImpl, '2.0.0');

function getInitials(name?: string) {
    const parts = name?.split(' ') ?? [];

    if (parts.length === 1) {
        return parts[0]?.toUpperCase().slice(0, 2);
    }

    return parts
        .map((word) => word[0]?.replace(/[^a-zA-Z]/g, '').toUpperCase())
        .filter(Boolean)
        .filter((_, i, arr) => i === 0 || i === arr.length - 1)
        .join('');
}
